import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getCatalogBrands, searchCatalog } from '../../services/bagService';
import { CatalogItem, CatalogSelection, CatalogTable } from '../../types/bag.types';

export interface CatalogPickerProps {
  table: CatalogTable;
  value: {
    catalog_id?: number | null;
    free_text_brand?: string | null;
    free_text_model?: string | null;
  };
  onSelect: (selection: CatalogSelection | null) => void;
  disabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

function describe(item: CatalogItem): string {
  const bits = [item.model];
  if (item.year) bits.push(String(item.year));
  return bits.join(' · ');
}

/**
 * Brand first, then model (MY_BAG_PLAN.md §4). Only irons and wedges reach this
 * component: `woods` holds 534 rows and every one of them is a driver (§9.4), so
 * a wood or hybrid slot gets free text from `SlotRow` and never a dropdown.
 *
 * When the catalog itself is unreachable the picker says so and stands down —
 * the slot's free-text brand/model fields are the fallback, and they live on
 * `SlotRow` because `CatalogSelection` cannot carry an unbound name.
 */
export default function CatalogPicker({
  table,
  value,
  onSelect,
  disabled = false,
}: CatalogPickerProps): JSX.Element {
  const [brands, setBrands] = useState<string[]>([]);
  const [brandsFailed, setBrandsFailed] = useState(false);
  const [brand, setBrand] = useState<string>(value.free_text_brand || '');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  const seqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setBrandsFailed(false);
    getCatalogBrands(table)
      .then((list) => {
        if (!cancelled) setBrands(list);
      })
      .catch(() => {
        if (!cancelled) {
          setBrands([]);
          setBrandsFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [table]);

  useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    if (!brand && trimmed.length < MIN_QUERY) {
      setResults([]);
      return undefined;
    }
    const seq = ++seqRef.current;
    setSearching(true);
    const timer = window.setTimeout(() => {
      searchCatalog(table, {
        manufacturer: brand || undefined,
        model: trimmed || undefined,
        limit: 25,
      })
        .then((rows) => {
          if (seq === seqRef.current) setResults(rows);
        })
        .catch(() => {
          if (seq === seqRef.current) setResults([]);
        })
        .finally(() => {
          if (seq === seqRef.current) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [table, brand, query, open]);

  const choose = useCallback(
    (item: CatalogItem) => {
      onSelect({
        catalog_table: table,
        catalog_id: item.id,
        free_text_brand: item.manufacturer,
        free_text_model: item.model,
        loft: item.loft ?? null,
      });
      setOpen(false);
      setQuery('');
      setResults([]);
    },
    [onSelect, table]
  );

  const bound = value.catalog_id != null;

  if (bound && !open) {
    const name = [value.free_text_brand, value.free_text_model].filter(Boolean).join(' ');
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-brand-sm border border-line-2 bg-ground px-2 py-1 text-sm text-ink">
          {name || `catalog #${value.catalog_id}`}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="min-h-[44px] rounded-brand border border-line-2 px-3 text-sm text-ink-2 hover:border-line-strong disabled:opacity-50"
        >
          Change
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSelect(null)}
          className="min-h-[44px] rounded-brand px-2 text-sm text-ink-3 underline hover:text-ink-2 disabled:opacity-50"
        >
          Clear
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="min-h-[44px] w-full rounded-brand border border-dashed border-line-2 px-3 text-left text-sm text-ink-3 hover:border-line-strong hover:text-ink-2 disabled:opacity-50"
      >
        {value.free_text_brand || value.free_text_model
          ? `${[value.free_text_brand, value.free_text_model].filter(Boolean).join(' ')} — find it in the catalog`
          : `Find your ${table === 'irons' ? 'irons' : 'wedge'} in the catalog`}
      </button>
    );
  }

  return (
    <div className="rounded-brand border border-line-2 bg-paper p-2">
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          disabled={disabled || brandsFailed}
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          className="min-h-[44px] rounded-brand border border-line-2 bg-paper px-2 text-sm text-ink sm:w-44"
        >
          <option value="">Any brand</option>
          {brands.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <input
          type="text"
          disabled={disabled}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Model"
          className="min-h-[44px] flex-1 rounded-brand border border-line-2 bg-paper px-2 text-sm text-ink placeholder:text-ink-3"
        />
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setQuery('');
          }}
          className="min-h-[44px] rounded-brand px-3 text-sm text-ink-3 underline hover:text-ink-2"
        >
          Cancel
        </button>
      </div>

      {brandsFailed && (
        <p className="mt-2 text-xs text-ink-3">
          The catalog is unavailable. Type the brand and model in the fields below instead — a
          free-text club still gaps, it just can&apos;t carry a catalog loft.
        </p>
      )}

      {!brand && query.trim().length < MIN_QUERY && !brandsFailed && (
        <p className="mt-2 text-xs text-ink-3">Pick a brand, or type at least two letters.</p>
      )}

      {searching && <p className="mt-2 text-xs text-ink-3">Searching…</p>}

      {!searching && results.length > 0 && (
        <ul className="mt-2 max-h-64 divide-y divide-line overflow-y-auto rounded-brand-sm border border-line">
          {results.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => choose(item)}
                className="flex min-h-[44px] w-full items-center justify-between gap-3 px-2 py-2 text-left text-sm hover:bg-ground"
              >
                <span className="text-ink">
                  <span className="text-ink-2">{item.manufacturer}</span> {describe(item)}
                </span>
                {item.loft != null && (
                  <span className="font-mono text-xs tabular-nums text-ink-3">{item.loft}°</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!searching && results.length === 0 && (brand || query.trim().length >= MIN_QUERY) && (
        <p className="mt-2 text-xs text-ink-3">
          Nothing matched. Type the brand and model in the fields below instead.
        </p>
      )}
    </div>
  );
}
